// src/pages/user/UserEnrolment.tsx
// Route: /user/enrolments

import { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  useGetEnrolmentsQuery,
  useGetBillingOptionsQuery,
  useRequestEnrolmentMutation,
  useCancelEnrolmentMutation,
} from "../../app/api/enrolmentApi";
import type { Enrolment, ChildBillingOptions, BillingOption, BillingPeriod, DeliveryMode, DeliveryModeOption } from "../../app/api/enrolmentApi";
import { useInitializePaymentMutation, useVerifyPaymentMutation } from "../../app/api/paymentApi";
import { useGetParentProfileQuery, useUpdateParentProfileMutation } from "../../app/api/parentApi";
import type { ParentLocation } from "../../app/api/parentApi";
import { DeliveryModePicker, LocationFields } from "../../components/DeliveryModePicker";

import { ArrowLeft, ArrowRight, CalendarDays, Check, CircleCheck, ClipboardList, CreditCard, GraduationCap, Hourglass, PartyPopper, Play, TriangleAlert, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import Ico from "../../components/Ico";
/* ─── CONSTANTS ──────────────────────────────────────────────────── */

const TRACK_COLORS: Record<string, { bg: string; text: string; border: string; accent: string }> = {
  foundation_builders: { bg: "#fff4f1", text: "#7a3020", border: "#f5c5b0", accent: "#efb0a4" },
  explorers:           { bg: "#ebeff7", text: "#4a44b0", border: "#cdd4e2", accent: "#1b2d5e" },
  innovators:          { bg: "#e8ecf5", text: "#1b2d5e", border: "#c9cfe0", accent: "#4a44b0" },
  unassigned:          { bg: "#f1f3f8", text: "#8189a0", border: "#dde2ec", accent: "#a6adbf" },
};
const TRACK_SHORT: Record<string, string> = {
  foundation_builders: "Foundation (6–9)",
  explorers:           "Explorers (9–12)",
  innovators:          "Innovators (13–18)",
  unassigned:          "Unassigned",
};

const ENROL_STATUS_MAP: Record<string, { label: string; cls: string; icon: LucideIcon }> = {
  pending:   { label: "Pending payment", cls: "bg-[#fff8ee] text-[#7a4a10] border border-[#f5d9a0]",  icon: Hourglass },
  confirmed: { label: "Confirmed",       cls: "bg-[#edfaf4] text-[#2a7a56] border border-[#b6e8d0]",  icon: CircleCheck },
  completed: { label: "Completed",       cls: "bg-[#ebeff7] text-[#4a44b0] border border-[#cdd4e2]",  icon: GraduationCap },
  withdrawn: { label: "Withdrawn",       cls: "bg-[#fff3f3] text-[#9b2a2a] border border-[#f5c0c0]",  icon: X },
};
const PAY_STATUS_MAP: Record<string, { label: string; cls: string }> = {
  paid:    { label: "Paid",    cls: "bg-[#edfaf4] text-[#2a7a56] border border-[#b6e8d0]" },
  pending: { label: "Pending", cls: "bg-[#fff8ee] text-[#7a4a10] border border-[#f5d9a0]" },
  overdue: { label: "Overdue", cls: "bg-[#fff3f3] text-[#9b2a2a] border border-[#f5c0c0]" },
  waived:  { label: "Waived",  cls: "bg-[#ebeff7] text-[#4a44b0] border border-[#cdd4e2]" },
};

function fmt(n: number) { return `₦${Number(n).toLocaleString("en-NG")}`; }

/** The discounts on one billing option: the billing period's, then the family discount. */
function DiscountLines({ opt }: { opt: BillingOption }) {
  return (
    <>
      {opt.discountPercent > 0 && (
        <div className="flex justify-between text-[#6cc2a0] font-semibold">
          <span>{opt.label} discount ({opt.discountPercent}%)</span>
          <span>−{fmt(opt.discountAmount)}</span>
        </div>
      )}
      {opt.familyDiscountPercent > 0 && (
        <div className="flex justify-between text-[#6cc2a0] font-semibold">
          <span>Family discount ({opt.familyDiscountPercent}%)</span>
          <span>−{fmt(opt.familyDiscountAmount)}</span>
        </div>
      )}
    </>
  );
}

/* ─── ATOMS ──────────────────────────────────────────────────────── */

function Chip({ label, cls }: { label: string; cls: string }) {
  return <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[12.5px] font-semibold ${cls}`}>{label}</span>;
}
function Skeleton({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse rounded-xl bg-[#ebeff7] ${className}`} />;
}
function Spinner({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
    </svg>
  );
}
function ProgressBar({ pct, color = "#1b2d5e" }: { pct: number; color?: string }) {
  return (
    <div className="h-1.5 overflow-hidden rounded-full bg-[#ebeff7]">
      <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.min(100, Math.max(0, pct))}%`, background: color }} />
    </div>
  );
}

/* ─── PAYMENT VERIFY BANNER ──────────────────────────────────────── */

function PaymentVerifyBanner({ reference, onDone }: { reference: string; onDone: () => void }) {
  const [verify, { isLoading, data, isError }] = useVerifyPaymentMutation();
  useEffect(() => { verify({ reference }); }, [reference]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) return (
    <div className="flex items-center gap-3 rounded-[20px] border border-[#cdd4e2] bg-[#f1f3f8] px-5 py-4">
      <Spinner className="h-4 w-4 text-[#1b2d5e]" />
      <p className="text-sm font-medium text-[#3b4560]">Verifying your payment…</p>
    </div>
  );
  if (isError) return (
    <div className="flex items-start gap-3 rounded-[20px] border border-[#f5d9a0] bg-[#fff8ee] px-5 py-4">
      <TriangleAlert className="h-5 w-5 flex-shrink-0" aria-hidden />
      <div className="flex-1">
        <p className="text-sm font-bold text-[#7a4a10]">Payment verification pending</p>
        <p className="mt-0.5 text-xs text-[#9a6a20]">It may take a moment to process — refresh in a few minutes.</p>
      </div>
      <button onClick={onDone} className="text-[#8189a0]"><X className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden /></button>
    </div>
  );
  const isPaid = data?.data?.payment?.status === "paid";
  const childName = (data?.data?.enrolment?.child as any)?.firstName ?? "your child";
  return (
    <div className={`flex items-start gap-3 rounded-[20px] border px-5 py-4 ${isPaid ? "border-[#b6e8d0] bg-[#edfaf4]" : "border-[#f5d9a0] bg-[#fff8ee]"}`}>
      {isPaid ? <PartyPopper className="h-6 w-6 flex-shrink-0" aria-hidden /> : <Hourglass className="h-6 w-6 flex-shrink-0" aria-hidden />}
      <div className="flex-1">
        <p className={`text-sm font-bold ${isPaid ? "text-[#2a7a56]" : "text-[#7a4a10]"}`}>
          {isPaid ? `Enrolment confirmed for ${childName}!` : "Payment received — processing confirmation"}
        </p>
        <p className={`mt-0.5 text-xs ${isPaid ? "text-[#3a8a66]" : "text-[#9a6a20]"}`}>
          {isPaid ? `Reference: ${reference}. Your child is now enrolled.` : "Refresh the page to check."}
        </p>
      </div>
      <button onClick={onDone} className="text-[#8189a0]"><X className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden /></button>
    </div>
  );
}

/* ─── BILLING OPTION CARD ────────────────────────────────────────── */

function BillingOptionCard({
  option, selected, onClick,
}: { option: BillingOption; selected: boolean; onClick: () => void }) {
  const isUpgrade    = (option as any).isUpgrade;
  const isSequential = (option as any).isSequential;
  const isCovered    = option.alreadyCovered;

  const startLabel = option.coverageStartDate
    ? new Date(option.coverageStartDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
    : null;

  return (
    <button type="button" onClick={() => !isCovered && onClick()}
      className={[
        "w-full rounded-[18px] border-2 p-4 text-left transition-all",
        selected   ? "border-[#1b2d5e] bg-[#ebeff7] shadow-[0_0_0_4px_rgba(27,45,94,0.10)]" :
        isCovered  ? "border-[#b6e8d0] bg-[#edfaf4] opacity-70 cursor-default" :
                     "border-[#dde2ec] bg-white hover:border-[#c9cfe0]",
      ].join(" ")}>

      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={["flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-[6px] border-2 transition-all",
            selected ? "border-[#1b2d5e] bg-[#1b2d5e]" : "border-[#c9cfe0] bg-white"].join(" ")}>
            {selected && <svg className="h-3 w-3 text-white" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
          </span>
          <p className="text-sm font-bold text-[#16213e]">{option.label}</p>
          {option.highlight && !isCovered && (
            <span className="rounded-full bg-[#1b2d5e] px-1.5 py-0.5 text-[11px] font-bold text-white">BEST VALUE</span>
          )}
          {isCovered && (
            <span className="rounded-full bg-[#edfaf4] px-1.5 py-0.5 text-[11px] font-bold text-[#2a7a56] border border-[#b6e8d0]">ALREADY COVERED</span>
          )}
          {isUpgrade && !isCovered && (
            <span className="rounded-full bg-[#ebeff7] px-1.5 py-0.5 text-[11px] font-bold text-[#4a44b0] border border-[#cdd4e2]">UPGRADE</span>
          )}
        </div>
        <div className="text-right flex-shrink-0">
          {isCovered
            ? <p className="text-sm font-bold text-[#2a7a56]">No payment needed</p>
            : <p className="text-sm font-bold text-[#16213e]">{fmt(option.amountDue)}</p>
          }
          {isUpgrade && !isCovered && option.creditApplied > 0 && (
            <p className="text-[11.5px] text-[#8189a0] line-through">{fmt(option.totalAmount)}</p>
          )}
        </div>
      </div>

      {/* Details */}
      <div className="mt-2.5 space-y-1.5 pl-7">
        <p className="text-[12.5px] text-[#5f6780]">
          {option.months} month{option.months > 1 ? "s" : ""} coverage
          {option.discountPercent > 0 && (
            <span className="ml-1.5 font-semibold text-[#6cc2a0]">· {option.discountPercent}% off</span>
          )}
          {option.familyDiscountPercent > 0 && (
            <span className="ml-1.5 font-semibold text-[#6cc2a0]">· family {option.familyDiscountPercent}% off</span>
          )}
        </p>

        {/* UPGRADE: show credit breakdown */}
        {isUpgrade && !isCovered && option.creditApplied > 0 && (
          <div className="rounded-[10px] border border-[#cdd4e2] bg-[#f1f3f8] px-2.5 py-2 text-[12.5px] space-y-0.5">
            <div className="flex justify-between text-[#5a6278]">
              <span>Full {option.label.toLowerCase()} price</span>
              <span className="font-semibold">{fmt(option.totalAmount)}</span>
            </div>
            <div className="flex justify-between text-[#2a7a56] font-semibold">
              <span>Credit from current payment</span>
              <span>−{fmt(option.creditApplied)}</span>
            </div>
            <div className="flex justify-between font-bold text-[#16213e] border-t border-[#cdd4e2] pt-1 mt-1">
              <span>You pay now</span>
              <span className="text-[#4a44b0]">{fmt(option.amountDue)}</span>
            </div>
          </div>
        )}

        {/* SEQUENTIAL: starts after current coverage */}
        {isSequential && startLabel && (
          <div className="rounded-[10px] border border-[#dde2ec] bg-[#f9fafc] px-2.5 py-2 text-[12.5px]">
            <p className="font-semibold text-[#4a44b0]"><Play className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Starts {startLabel}</p>
            <p className="text-[#8189a0] mt-0.5">Begins the day after your current coverage ends.</p>
          </div>
        )}

        {/* Already covered */}
        {isCovered && (
          <p className="text-[12.5px] text-[#2a7a56] font-semibold">
            <Check className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Current plan already covers {option.existingMonthsRemaining} more month{option.existingMonthsRemaining !== 1 ? "s" : ""}
          </p>
        )}

        {/* Covers until */}
        {option.coverageEndLabel && !isCovered && (
          <p className="text-[11.5px] text-[#8189a0]">
            <CalendarDays className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Covers until <span className="font-semibold text-[#3b4560]">{option.coverageEndLabel}</span>
          </p>
        )}
      </div>
    </button>
  );
}



/* ─── NEW ENROLMENT DRAWER ───────────────────────────────────────── */

function NewEnrolmentDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: optionsData, isLoading: optionsLoading } = useGetBillingOptionsQuery(undefined, { skip: !open });
  const [requestEnrolment,  { isLoading: enrolling }]  = useRequestEnrolmentMutation();
  const [initializePayment, { isLoading: payLoading }] = useInitializePaymentMutation();
  const { data: profileData } = useGetParentProfileQuery(undefined, { skip: !open });
  const [updateProfile, { isLoading: savingLocation }] = useUpdateParentProfileMutation();
  const profile = profileData?.data;

  // Online is the only open mode at launch; hybrid and on-site are "Coming soon".
  const [mode, setMode] = useState<DeliveryMode>("online");
  const [location, setLocation] = useState<ParentLocation>({ state: "", city: "", area: null });
  useEffect(() => {
    if (profile?.location) setLocation(profile.location);
  }, [profile?.location]);

  const [step, setStep]         = useState<"select" | "review" | "paying">("select");
  const [serverError, setServerError] = useState("");

  // Per-child selected billing period
  const [selections, setSelections] = useState<Record<string, BillingPeriod>>({});
  // Which children are checked
  const [checked, setChecked]       = useState<Record<string, boolean>>({});

  const children: ChildBillingOptions[] = useMemo(() => optionsData?.data ?? [], [optionsData]);

  // One mode list for the drawer: a mode is open if it is open for the
  // children shown; the fee shown is the lowest (fees differ by age group).
  const modes: DeliveryModeOption[] = useMemo(() => {
    const byMode = new Map<DeliveryMode, DeliveryModeOption>();
    for (const c of children) {
      for (const m of c.modes ?? []) {
        const prev = byMode.get(m.mode);
        byMode.set(m.mode, prev
          ? { ...prev, open: prev.open || m.open,
              monthlyFee: prev.monthlyFee == null ? m.monthlyFee
                : m.monthlyFee == null ? prev.monthlyFee
                : Math.min(prev.monthlyFee, m.monthlyFee) }
          : m);
      }
    }
    return Array.from(byMode.values());
  }, [children]);

  // Initialise defaults when options load
  useEffect(() => {
    if (children.length > 0) {
      const newSel: Record<string, BillingPeriod> = {};
      const newChk: Record<string, boolean>       = {};
      children.forEach(c => {
        const cid = String(c.childId);
        // Default to quarterly unless already covered for quarterly
        const qOpt = c.options.find(o => o.billingPeriod === "quarterly");
        newSel[cid] = qOpt && !qOpt.alreadyCovered ? "quarterly" : "monthly";
        newChk[cid] = children.length === 1;
      });
      setSelections(s => ({ ...newSel, ...s }));
      setChecked(k => ({ ...newChk, ...k }));
    }
  }, [children.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedChildren = children.filter(c => checked[String(c.childId)]);

  const getOption = (c: ChildBillingOptions, period: BillingPeriod) =>
    c.options.find(o => o.billingPeriod === period)!;

  const totalDue = selectedChildren.reduce((sum, c) => {
    const opt = getOption(c, selections[String(c.childId)] ?? "quarterly");
    return sum + (opt?.amountDue ?? 0);
  }, 0);

  const reset = () => { setStep("select"); setServerError(""); };
  const handleClose = () => { reset(); onClose(); };

  const handleContinue = async () => {
    if (selectedChildren.length === 0) { setServerError("Select at least one child."); return; }
    const unassigned = selectedChildren.find(c => c.track === "unassigned");
    if (unassigned) { setServerError(`${unassigned.firstName}'s track hasn't been assigned yet.`); return; }
    if (!location.state || !location.city.trim()) {
      setServerError("Tell us where you live (state and city) before you continue.");
      return;
    }
    const saved = profile?.location;
    const changed = !saved || saved.state !== location.state ||
      saved.city !== location.city.trim() || (saved.area ?? "") !== (location.area ?? "").trim();
    if (changed) {
      try {
        await updateProfile({ location: { ...location, city: location.city.trim(), area: location.area?.trim() || null } }).unwrap();
      } catch (err: any) {
        setServerError(err?.data?.error ?? "Could not save your location.");
        return;
      }
    }
    setServerError("");
    setStep("review");
  };

  const handleConfirmAndPay = async () => {
    setServerError(""); setStep("paying");
    const ids: string[] = [];

    for (const child of selectedChildren) {
      const cid    = String(child.childId);
      const period = selections[cid] ?? "quarterly";
      const opt    = getOption(child, period);
      if (opt?.alreadyCovered) continue; // skip — already paid
      try {
        const result = await requestEnrolment({
          childId: cid, billingPeriod: period, deliveryMode: mode,
        }).unwrap();
        ids.push(result.data.id);
      } catch (err: any) {
        setServerError(err?.data?.error ?? `Failed to create enrolment for ${child.firstName}.`);
        setStep("review"); return;
      }
    }

    if (ids.length === 0) {
      // All children are already covered
      setServerError(""); handleClose(); return;
    }

    try {
      const result = await initializePayment({ enrolmentId: ids[0] }).unwrap();
      const queue  = ids.slice(1);
      if (queue.length > 0) {
        localStorage.setItem("fsl_payment_queue", JSON.stringify({
          queue, childNames: selectedChildren.slice(1).map(c => c.firstName),
          totalCount: ids.length, paidCount: 0,
        }));
      } else {
        localStorage.removeItem("fsl_payment_queue");
      }
      window.location.href = result.data.authorizationUrl;
    } catch (err: any) {
      setServerError(err?.data?.error ?? "Payment initiation failed."); setStep("review");
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-[#16213e]/40 backdrop-blur-sm" onClick={handleClose} />
      <div className="fixed inset-y-0 right-0 z-50 flex w-full flex-col bg-white shadow-[-8px_0_40px_rgba(27,45,94,0.12)] sm:max-w-[520px]"
        role="dialog" aria-modal>

        {/* Header */}
        <div className="flex flex-shrink-0 items-center justify-between border-b border-[#ebeff7] px-6 py-5">
          <div>
            <h2 className="text-lg font-bold tracking-[-0.03em] text-[#16213e]">
              {step === "select" ? "Enrol a child" : step === "review" ? "Review & pay" : "Processing…"}
            </h2>
            <p className="mt-0.5 text-xs text-[#8189a0]">
              {step === "select" ? "Choose how your child learns, then a billing plan." :
               step === "review" ? "Confirm before payment." : "Creating enrolments…"}
            </p>
          </div>
          <button onClick={handleClose} className="flex h-8 w-8 items-center justify-center rounded-xl text-[#8189a0] hover:bg-[#f1f3f8]">
            <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          </button>
        </div>

        {/* Paying spinner */}
        {step === "paying" && (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center px-6">
            <div className="relative flex h-16 w-16 items-center justify-center">
              <div className="absolute h-16 w-16 animate-spin rounded-full border-4 border-[#ebeff7] border-t-[#1b2d5e]" />
              <span className="relative"><CreditCard className="h-6 w-6" aria-hidden /></span>
            </div>
            <p className="text-base font-bold text-[#16213e]">Creating enrolments…</p>
          </div>
        )}

        {/* STEP 1 — SELECT */}
        {step === "select" && (
          <div className="flex flex-1 flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              {serverError && (
                <div className="rounded-2xl border border-[#fde0d9] bg-[#fff5f3] px-4 py-3 text-sm text-[#b94a3a]">{serverError}</div>
              )}
              {!optionsLoading && children.length > 0 && modes.length > 0 && (
                <>
                  <DeliveryModePicker
                    modes={modes}
                    value={mode}
                    onChange={setMode}
                    location={profile?.location ?? null}
                    interest={profile?.onsiteInterest?.modes ?? []}
                  />
                  <LocationFields value={location} onChange={setLocation} />
                </>
              )}
              {optionsLoading ? (
                Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-32 rounded-[20px]" />)
              ) : children.length === 0 ? (
                <div className="rounded-[20px] border border-dashed border-[#cdd4e2] bg-[#f9fafc] p-6 text-center">
                  <p className="text-sm text-[#8189a0]">No children added yet.</p>
                  <Link to="/user/children" className="mt-2 inline-block text-sm font-semibold text-[#1b2d5e]">Add a child first{" "}<ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden /></Link>
                </div>
              ) : children.map(child => {
                const cid     = String(child.childId);
                const tc      = TRACK_COLORS[child.track] ?? TRACK_COLORS.unassigned;
                const isChk   = checked[cid] ?? false;
                const selPer  = selections[cid] ?? "quarterly";
                const selOpt  = getOption(child, selPer);

                return (
                  <div key={cid} className={["rounded-[22px] border-2 overflow-hidden transition-all",
                    isChk ? "border-[#1b2d5e] shadow-[0_0_0_4px_rgba(27,45,94,0.08)]" : "border-[#dde2ec]"].join(" ")}>
                    {/* Child header */}
                    <button type="button" onClick={() => setChecked(prev => ({ ...prev, [cid]: !prev[cid] }))}
                      className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-[#f9fafc]">
                      <span className={["flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-[6px] border-2 transition-all",
                        isChk ? "border-[#1b2d5e] bg-[#1b2d5e]" : "border-[#c9cfe0] bg-white"].join(" ")}>
                        {isChk && <svg className="h-3 w-3 text-white" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                      </span>
                      <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[11px] text-xs font-bold"
                        style={{ background: tc.bg, color: tc.text, border: `1px solid ${tc.border}` }}>
                        {child.firstName.charAt(0)}{child.lastName.charAt(0)}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-[#16213e]">{child.firstName} {child.lastName}</p>
                        <p className="text-[12.5px]" style={{ color: tc.text }}>{child.trackLabel}</p>
                        {!!child.familyDiscount?.percent && (
                          <p className="text-[12px] font-semibold text-[#2a7a56]">{child.familyDiscount.reason}</p>
                        )}
                      </div>
                      {isChk && selOpt && !selOpt.alreadyCovered && (
                        <span className="text-sm font-bold text-[#4a44b0]">{fmt(selOpt.amountDue)}</span>
                      )}
                      {isChk && selOpt?.alreadyCovered && (
                        <span className="text-[12.5px] font-bold text-[#2a7a56]">Already covered</span>
                      )}
                    </button>

                    {/* Current coverage banner */}
                    {child.currentCoverage && (
                      <div className="mx-4 mb-3 mt-1 rounded-[14px] border border-[#b6e8d0] bg-[#edfaf4] px-3 py-2.5">
                        <p className="text-[12.5px] font-semibold text-[#2a7a56]">
                          <Check className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Active {child.currentCoverage.billingLabel} plan · {child.currentCoverage.monthsRemaining} month{child.currentCoverage.monthsRemaining > 1 ? "s" : ""} remaining
                        </p>
                        <p className="text-[11.5px] text-[#6cc2a0] mt-0.5">
                          Coverage expires {child.currentCoverage.endLabel}
                        </p>
                      </div>
                    )}

                    {/* Billing options (only when checked) */}
                    {isChk && (
                      <div className="border-t border-[#ebeff7] px-4 pb-4 pt-3 space-y-2">
                        <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0] mb-2">
                          {child.currentCoverage ? "Extend or upgrade plan" : "Choose billing plan"}
                        </p>
                        {child.options.map(opt => (
                          <BillingOptionCard
                            key={opt.billingPeriod}
                            option={opt}
                            selected={selPer === opt.billingPeriod}
                            onClick={() => !opt.alreadyCovered && setSelections(prev => ({ ...prev, [cid]: opt.billingPeriod }))}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex-shrink-0 border-t border-[#ebeff7] px-6 py-5">
              {selectedChildren.length > 0 && totalDue > 0 && (
                <div className="mb-3 flex items-center justify-between rounded-[16px] bg-[#ebeff7] px-4 py-3">
                  <p className="text-xs font-semibold text-[#3b4560]">{selectedChildren.length} child{selectedChildren.length > 1 ? "ren" : ""} · payment due</p>
                  <p className="text-sm font-bold text-[#4a44b0]">{fmt(totalDue)}</p>
                </div>
              )}
              {selectedChildren.length > 0 && totalDue === 0 && (
                <div className="mb-3 flex items-center justify-between rounded-[16px] bg-[#edfaf4] border border-[#b6e8d0] px-4 py-3">
                  <p className="text-xs font-semibold text-[#2a7a56]">All selected children are already covered</p>
                  <Check className="h-5 w-5 flex-shrink-0" aria-hidden />
                </div>
              )}
              <div className="flex gap-3">
                <button type="button" onClick={handleClose} className="flex-1 rounded-2xl border border-[#dde2ec] py-3 text-sm font-semibold text-[#5a6278] hover:bg-[#f1f3f8]">Cancel</button>
                <button type="button" onClick={handleContinue} disabled={selectedChildren.length === 0 || savingLocation}
                  className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-[#1b2d5e] py-3 text-sm font-semibold text-white shadow-[0_8px_24px_rgba(27,45,94,0.30)] hover:bg-[#24397a] disabled:opacity-50">
                  Review{" "}<ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 2 — REVIEW */}
        {step === "review" && (
          <div className="flex flex-1 flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
              {serverError && (
                <div className="rounded-2xl border border-[#fde0d9] bg-[#fff5f3] px-4 py-3 text-sm text-[#b94a3a]">{serverError}</div>
              )}
              <div className="rounded-[16px] border border-[#cdd4e2] bg-[#f1f3f8] px-4 py-3 text-xs text-[#3b4560]">
                <b>{modes.find(m => m.mode === mode)?.label ?? "Online"} learning</b>
                {" · "}{location.city}{location.area ? `, ${location.area}` : ""}, {location.state}
              </div>
              {selectedChildren.map(child => {
                const cid  = String(child.childId);
                const opt  = getOption(child, selections[cid] ?? "quarterly");
                const tc   = TRACK_COLORS[child.track] ?? TRACK_COLORS.unassigned;
                if (!opt) return null;
                return (
                  <div key={cid} className="rounded-[20px] border border-[#dde2ec] overflow-hidden">
                    <div className="flex items-center gap-3 px-4 py-3 border-b border-[#ebeff7]"
                      style={{ background: `linear-gradient(90deg, ${tc.bg}, white)` }}>
                      <span className="flex h-8 w-8 items-center justify-center rounded-[10px] text-[12.5px] font-bold flex-shrink-0"
                        style={{ background: tc.bg, color: tc.text, border: `1px solid ${tc.border}` }}>
                        {child.firstName.charAt(0)}{child.lastName.charAt(0)}
                      </span>
                      <div className="flex-1">
                        <p className="text-sm font-bold text-[#16213e]">{child.firstName} {child.lastName}</p>
                        <p className="text-[11.5px]" style={{ color: tc.text }}>{child.trackLabel}</p>
                        {!!child.familyDiscount?.percent && (
                          <p className="text-[11.5px] font-semibold text-[#2a7a56]">{child.familyDiscount.reason}</p>
                        )}
                      </div>
                      {opt.alreadyCovered
                        ? <span className="text-[12.5px] font-bold text-[#2a7a56]">No payment</span>
                        : <span className="text-sm font-bold text-[#16213e]">{fmt(opt.amountDue)}</span>
                      }
                    </div>
                    <div className="px-4 py-3 space-y-1.5 text-xs">
                      {opt.alreadyCovered ? (
                        <p className="text-[#2a7a56] font-semibold"><Check className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Already covered by current plan</p>
                      ) : (opt as any).isUpgrade ? (
                        /* Upgrade: full price → credit → amount due */
                        <>
                          <div className="flex justify-between text-[#5a6278]">
                            <span>{opt.label} · {opt.months} month{opt.months > 1 ? "s" : ""}</span>
                            <span>{fmt(opt.grossAmount)}</span>
                          </div>
                          <DiscountLines opt={opt} />
                          {opt.creditApplied > 0 && (
                            <div className="flex justify-between text-[#2a7a56] font-semibold">
                              <span>Credit from current payment</span>
                              <span>−{fmt(opt.creditApplied)}</span>
                            </div>
                          )}
                          <div className="flex justify-between font-bold text-[#16213e] border-t border-[#ebeff7] pt-1.5">
                            <span>You pay now</span>
                            <span className="text-[#4a44b0]">{fmt(opt.amountDue)}</span>
                          </div>
                          {opt.coverageEndLabel && (
                            <div className="flex justify-between text-[#8189a0] pt-0.5">
                              <span>Covers until</span>
                              <span className="font-semibold text-[#1b2d5e]"><CalendarDays className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}{opt.coverageEndLabel}</span>
                            </div>
                          )}
                        </>
                      ) : (
                        /* Sequential: full price, starts after current */
                        <>
                          <div className="flex justify-between text-[#5a6278]">
                            <span>{opt.label} · {opt.months} month{opt.months > 1 ? "s" : ""}</span>
                            <span>{fmt(opt.grossAmount)}</span>
                          </div>
                          <DiscountLines opt={opt} />
                          <div className="flex justify-between font-bold text-[#16213e] border-t border-[#ebeff7] pt-1.5">
                            <span>You pay</span>
                            <span>{fmt(opt.amountDue)}</span>
                          </div>
                          {opt.coverageStartDate && (
                            <div className="flex justify-between text-[#4a44b0] font-semibold pt-0.5">
                              <span><Play className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Starts</span>
                              <span>{new Date(opt.coverageStartDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                            </div>
                          )}
                          {opt.coverageEndLabel && (
                            <div className="flex justify-between text-[#8189a0] pt-0.5">
                              <span>Ends</span>
                              <span className="font-semibold text-[#1b2d5e]"><CalendarDays className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}{opt.coverageEndLabel}</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
              {/* Total block */}
              <div className="rounded-[20px] bg-[#16213e] px-5 py-4 text-white flex items-center justify-between">
                <div>
                  <p className="text-[11.5px] font-bold uppercase tracking-wider text-white/50">Total due now</p>
                  <p className="mt-1 text-2xl font-bold tracking-[-0.04em]">{fmt(totalDue)}</p>
                  {totalDue === 0 && <p className="text-xs text-white/60 mt-0.5">All children already covered</p>}
                </div>
                <CreditCard className="h-7 w-7 flex-shrink-0" aria-hidden />
              </div>
            </div>
            <div className="flex-shrink-0 border-t border-[#ebeff7] px-6 py-5 flex gap-3">
              <button type="button" onClick={() => { setStep("select"); setServerError(""); }}
                className="flex-1 rounded-2xl border border-[#dde2ec] py-3 text-sm font-semibold text-[#5a6278] hover:bg-[#f1f3f8]"><ArrowLeft className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Edit</button>
              <button type="button" onClick={handleConfirmAndPay} disabled={enrolling || payLoading}
                className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-[#1b2d5e] py-3 text-sm font-bold text-white shadow-[0_8px_24px_rgba(27,45,94,0.30)] hover:bg-[#24397a] disabled:opacity-60">
                {enrolling || payLoading ? <><Spinner />Processing…</> : totalDue === 0 ? "Confirm" : `Pay ${fmt(totalDue)}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

/* ─── ENROLMENT CARD ─────────────────────────────────────────────── */

function EnrolmentCard({ enrolment }: { enrolment: Enrolment }) {
  const [cancelEnrolment,   { isLoading: cancelling }]  = useCancelEnrolmentMutation();
  const [initializePayment, { isLoading: payLoading }]  = useInitializePaymentMutation();
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [payError,      setPayError]      = useState("");

  const tc         = TRACK_COLORS[enrolment.track] ?? TRACK_COLORS.unassigned;
  const statusInfo = ENROL_STATUS_MAP[enrolment.status]      ?? ENROL_STATUS_MAP.pending;
  const payInfo    = PAY_STATUS_MAP[enrolment.paymentStatus]  ?? PAY_STATUS_MAP.pending;
  const childName  = enrolment.child ? `${enrolment.child.firstName} ${enrolment.child.lastName}` : "Unknown";

  const isPending   = enrolment.status === "pending";
  const isConfirmed = enrolment.status === "confirmed";
  const isCompleted = enrolment.status === "completed";
  const isWithdrawn = enrolment.status === "withdrawn";

  const handlePayNow = async () => {
    setPayError("");
    try {
      const result = await initializePayment({ enrolmentId: enrolment.id }).unwrap();
      window.location.href = result.data.authorizationUrl;
    } catch (err: any) {
      setPayError(err?.data?.error ?? "Payment initiation failed.");
    }
  };

  const handleCancel = async () => {
    try { await cancelEnrolment(enrolment.id).unwrap(); setCancelConfirm(false); }
    catch (err: any) { console.error("Cancel failed:", err); }
  };

  // Coverage display
  const coverageEnd = (enrolment as any).coverageEndDate;
  const isTopUp     = (enrolment as any).isTopUp;
  const creditApplied = (enrolment as any).creditApplied ?? 0;

  return (
    <div className={`rounded-[24px] border border-[#e5e8ef] bg-white shadow-[0_4px_16px_rgba(27,45,94,0.05)] overflow-hidden`}>
      <div className="h-1 w-full" style={{ background: tc.accent }} />
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[12px] text-xs font-bold"
            style={{ background: tc.bg, color: tc.text, border: `1px solid ${tc.border}` }}>
            {enrolment.child ? `${enrolment.child.firstName.charAt(0)}${enrolment.child.lastName.charAt(0)}` : "?"}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-1.5">
              <p className="text-sm font-bold text-[#16213e]">{childName}</p>
              <span className="text-[#c9cfe0]">·</span>
              <p className="text-xs text-[#8189a0]">{enrolment.academicYear}</p>
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {enrolment.billingPeriod && (
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11.5px] font-bold ${
                  enrolment.billingPeriod === "monthly"   ? "bg-[#fff8ee] text-[#7a4a10] border border-[#f5d9a0]" :
                  enrolment.billingPeriod === "quarterly" ? "bg-[#ebeff7] text-[#4a44b0] border border-[#cdd4e2]" :
                                                            "bg-[#edfaf4] text-[#2a7a56] border border-[#b6e8d0]"}`}>
                  {enrolment.billingLabel ?? enrolment.billingPeriod}
                </span>
              )}
              {isTopUp && creditApplied > 0 && (
                <span className="text-[11.5px] font-semibold text-[#2a7a56] bg-[#edfaf4] border border-[#b6e8d0] rounded-full px-1.5 py-0.5">
                  {fmt(creditApplied)} credit applied
                </span>
              )}
              {enrolment.discountPercent > 0 && (
                <span className="text-[11.5px] font-semibold text-[#6cc2a0]">{enrolment.discountPercent}% off</span>
              )}
            </div>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[12.5px] font-semibold"
                style={{ background: tc.bg, color: tc.text, border: `1px solid ${tc.border}` }}>
                {TRACK_SHORT[enrolment.track] ?? enrolment.track}
              </span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[12.5px] font-semibold ${statusInfo.cls}`}>
              <Ico icon={statusInfo.icon} /> {statusInfo.label}
            </span>
            <Chip label={payInfo.label} cls={payInfo.cls} />
          </div>
        </div>

        {/* Coverage window */}
        {isConfirmed && coverageEnd && (
          <div className="mt-3 flex items-center gap-2 rounded-[12px] border border-[#cdd4e2] bg-[#ebeff7] px-3.5 py-2.5">
            <CalendarDays className="h-4 w-4 flex-shrink-0" aria-hidden />
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-[#5a6278]">Coverage until</p>
              <p className="text-xs font-bold text-[#16213e]">
                {new Date(coverageEnd).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
              </p>
            </div>
          </div>
        )}

        {/* Progress */}
        {isConfirmed && (
          <div className="mt-4">
            <div className="mb-1.5 flex justify-between text-xs">
              <span className="font-medium text-[#3b4560]">Week {enrolment.currentWeek} · {enrolment.currentProject ?? "In progress"}</span>
              <span className="font-bold" style={{ color: tc.accent }}>{enrolment.progressPercent}%</span>
            </div>
            <ProgressBar pct={enrolment.progressPercent} color={tc.accent} />
          </div>
        )}

        {/* Fee summary */}
        <div className="mt-3 rounded-[14px] border border-[#ebeff7] bg-[#f9fafc] px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">
                {enrolment.billingPeriod ? `${enrolment.billingPeriod.charAt(0).toUpperCase()}${enrolment.billingPeriod.slice(1)} plan` : "Fee"}
              </p>
              <p className="mt-0.5 text-base font-bold text-[#16213e]">{fmt(enrolment.feeAmount)}</p>
              {creditApplied > 0 && (
                <p className="text-[11.5px] text-[#2a7a56] font-semibold">Includes {fmt(creditApplied)} credit</p>
              )}
            </div>
            {enrolment.confirmedAt && (
              <div className="text-right">
                <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">Confirmed</p>
                <p className="text-xs font-semibold text-[#16213e]">
                  {new Date(enrolment.confirmedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                </p>
              </div>
            )}
          </div>
        </div>

        {payError && <p className="mt-2 rounded-xl bg-[#fff5f3] px-3 py-2 text-xs text-[#b94a3a]">{payError}</p>}

        {/* Actions */}
        {!isWithdrawn && !isCompleted && (
          <div className="mt-4 flex gap-2">
            {isPending && (
              <>
                <button onClick={handlePayNow} disabled={payLoading}
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-2xl bg-[#1b2d5e] py-2.5 text-xs font-bold text-white shadow-[0_4px_14px_rgba(27,45,94,0.28)] hover:bg-[#24397a] disabled:opacity-60">
                  {payLoading ? <Spinner className="h-3.5 w-3.5" /> : <CreditCard className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />} Pay now
                </button>
                {!cancelConfirm ? (
                  <button onClick={() => setCancelConfirm(true)}
                    className="rounded-2xl border border-[#dde2ec] px-4 py-2.5 text-xs font-semibold text-[#8189a0] hover:border-[#f5c0c0] hover:bg-[#fff5f5] hover:text-[#9b2a2a]">
                    Cancel
                  </button>
                ) : (
                  <div className="flex flex-1 items-center gap-1.5 rounded-2xl border border-[#f5c0c0] bg-[#fff5f5] px-3 py-2">
                    <p className="flex-1 text-[11.5px] font-semibold text-[#9b2a2a]">Confirm cancel?</p>
                    <button onClick={handleCancel} disabled={cancelling}
                      className="rounded-lg bg-[#e57373] px-2 py-1 text-[11.5px] font-bold text-white hover:bg-[#c0392b] disabled:opacity-60">
                      {cancelling ? "…" : "Yes"}
                    </button>
                    <button onClick={() => setCancelConfirm(false)}
                      className="rounded-lg border border-[#f5c0c0] px-2 py-1 text-[11.5px] font-semibold text-[#9b2a2a]">No</button>
                  </div>
                )}
              </>
            )}
            {isConfirmed && (
              <Link to={`/user/children/${enrolment.child?.id}`}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-2xl border border-[#dde2ec] py-2.5 text-xs font-semibold text-[#3b4560] hover:border-[#c9cfe0] hover:bg-[#f1f3f8]">
                View progress{" "}<ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
              </Link>
            )}
          </div>
        )}

        {(isCompleted || isWithdrawn) && (
          <div className={`mt-4 rounded-[14px] px-4 py-2.5 text-center text-xs font-medium ${isCompleted ? "bg-[#ebeff7] text-[#4a44b0]" : "bg-[#fff3f3] text-[#9b2a2a]"}`}>
            {isCompleted ? <><GraduationCap className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden /> Quarter completed. Well done!</> : <><X className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden /> This enrolment was withdrawn</>}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── ROOT PAGE ──────────────────────────────────────────────────── */

interface Filters { status: string; quarter: string; track: string; }

export default function UserEnrolment() {
  const [searchParams, setSearchParams] = useSearchParams();
  const paymentRef = searchParams.get("ref");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filters, setFilters]       = useState<Filters>({ status: "", quarter: "", track: "" });

  const { data, isLoading, isError, refetch } = useGetEnrolmentsQuery({});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allEnrolments = data?.data ?? [];

  const filtered = allEnrolments
    .filter(e => !filters.status  || e.status  === filters.status)
    .filter(e => !filters.quarter || e.quarter === filters.quarter)
    .filter(e => !filters.track   || e.track   === filters.track);

  const sorted = [...filtered].sort((a, b) => {
    const order: Record<string, number> = { pending: 0, confirmed: 1, completed: 2, withdrawn: 3 };
    if (order[a.status] !== order[b.status]) return order[a.status] - order[b.status];
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  const stats = useMemo(() => ({
    total:     allEnrolments.length,
    confirmed: allEnrolments.filter(e => e.status === "confirmed").length,
    pending:   allEnrolments.filter(e => e.status === "pending").length,
    completed: allEnrolments.filter(e => e.status === "completed").length,
  }), [allEnrolments]);

  const dismissRef = () => { searchParams.delete("ref"); setSearchParams(searchParams); };

  if (isError) return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 text-center">
      <p className="text-sm font-semibold text-[#16213e]">Couldn't load enrolments</p>
      <button onClick={refetch} className="rounded-2xl bg-[#1b2d5e] px-5 py-2.5 text-xs font-semibold text-white">Retry</button>
    </div>
  );

  return (
    <>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-[1.6rem] font-bold tracking-[-0.04em] text-[#16213e]">Enrolments</h1>
          <p className="mt-1 text-sm text-[#5f6780]">Manage your children's quarterly enrolments and payments.</p>
        </div>
        <button onClick={() => setDrawerOpen(true)}
          className="inline-flex flex-shrink-0 items-center gap-2 self-start rounded-2xl bg-[#1b2d5e] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_8px_24px_rgba(27,45,94,0.30)] hover:bg-[#24397a]">
          <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
          Enrol a child
        </button>
      </div>

      {paymentRef && (
        <div className="mb-5"><PaymentVerifyBanner reference={paymentRef} onDone={dismissRef} /></div>
      )}

      {/* Stats */}
      {!isLoading && allEnrolments.length > 0 && (
        <div className="grid grid-cols-4 gap-3 mb-5">
          {[
            { label: "Total",     value: stats.total,     color: "#1b2d5e", bg: "#ebeff7" },
            { label: "Active",    value: stats.confirmed, color: "#6cc2a0", bg: "#edfaf4" },
            { label: "Pending",   value: stats.pending,   color: "#f0a050", bg: "#fff8ee" },
            { label: "Completed", value: stats.completed, color: "#4a44b0", bg: "#e8ecf5" },
          ].map(s => (
            <div key={s.label} className="rounded-[18px] border border-[#e5e8ef] bg-white px-4 py-3 text-center shadow-[0_2px_8px_rgba(27,45,94,0.04)]">
              <p className="text-2xl font-bold tracking-[-0.04em]" style={{ color: s.color }}>{s.value}</p>
              <p className="mt-0.5 text-[11.5px] font-semibold uppercase tracking-wider text-[#8189a0]">{s.label}</p>
            </div>
          ))}
        </div>
      )}
      {isLoading && (
        <div className="grid grid-cols-4 gap-3 mb-5">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-[64px] rounded-[18px]" />)}
        </div>
      )}

      {/* Filters */}
      {!isLoading && allEnrolments.length > 0 && (
        <div className="mb-5 flex flex-wrap items-center gap-3">
          <p className="text-sm font-semibold text-[#16213e]">{sorted.length} enrolment{sorted.length !== 1 ? "s" : ""}</p>
          <div className="ml-auto flex flex-wrap gap-2">
            {[
              { value: filters.status,  onChange: (v: string) => setFilters(f => ({ ...f, status: v })),
                opts: [["", "All statuses"], ["pending","Pending"], ["confirmed","Confirmed"], ["completed","Completed"], ["withdrawn","Withdrawn"]] },
              { value: filters.quarter, onChange: (v: string) => setFilters(f => ({ ...f, quarter: v })),
                opts: [["", "All quarters"], ["Q1","Q1"], ["Q2","Q2"], ["Q3","Q3"]] },
              { value: filters.track,   onChange: (v: string) => setFilters(f => ({ ...f, track: v })),
                opts: [["", "All tracks"], ["foundation_builders","Foundation"], ["explorers","Explorers"], ["innovators","Innovators"]] },
            ].map((sel, i) => (
              <select key={i} value={sel.value} onChange={e => sel.onChange(e.target.value)}
                className="rounded-xl border border-[#dde2ec] bg-white px-3 py-2 text-xs font-semibold text-[#3b4560] outline-none focus:border-[#1b2d5e] cursor-pointer">
                {sel.opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            ))}
          </div>
        </div>
      )}

      {/* Grid */}
      <div className="mt-2">
        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-[220px] rounded-[24px]" />)}
          </div>
        ) : allEnrolments.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-[28px] border border-dashed border-[#cdd4e2] bg-[#f9fafc] px-6 py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#ebeff7]"><ClipboardList className="h-8 w-8" aria-hidden /></span>
            <h3 className="mt-5 text-lg font-bold tracking-[-0.03em] text-[#16213e]">No enrolments yet</h3>
            <p className="mt-2 max-w-xs text-sm leading-6 text-[#5f6780]">Enrol a child to get them started at Future Smart Lab.</p>
            <button onClick={() => setDrawerOpen(true)}
              className="mt-6 inline-flex items-center gap-2 rounded-2xl bg-[#1b2d5e] px-6 py-3 text-sm font-semibold text-white shadow-[0_8px_24px_rgba(27,45,94,0.30)] hover:bg-[#24397a]">
              + Enrol your first child
            </button>
          </div>
        ) : sorted.length === 0 ? (
          <div className="rounded-[24px] border border-[#e5e8ef] bg-[#f9fafc] py-12 text-center">
            <p className="text-sm font-semibold text-[#16213e]">No enrolments match this filter</p>
            <button onClick={() => setFilters({ status: "", quarter: "", track: "" })}
              className="mt-3 text-xs font-semibold text-[#1b2d5e]">Clear filters</button>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {sorted.map(e => <EnrolmentCard key={e.id} enrolment={e} />)}
          </div>
        )}
      </div>

      <NewEnrolmentDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </>
  );
}